import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { playerAPI } from '../services/api';
import { readJwtPayload } from '../contexts/AuthContext';
import { getLocalStorageItem } from '../utils/safeStorage';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1.5rem;
    width: calc(100% - 1rem);
    max-width: 400px;
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoText = styled.p`
  margin: 0;
  color: #0369a1;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 48px;
  }
`;

const PrimaryButton = styled(Button)`
  background: #043663;
  color: white;

  &:hover:not(:disabled) {
    background: #032a4f;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
`;

const PhoneNumberModal = ({ isOpen, onClose, onSuccess, teamId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isPlayerCaptain =
    readJwtPayload(getLocalStorageItem('token'))?.type === 'player';

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      setError(t('teamDashboardPage.phoneEnterError'));
      return;
    }

    const phoneRegex = /^[+]?[0-9\s\-()]{8,20}$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError(t('teamDashboardPage.phoneInvalidError'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let response;
      if (isPlayerCaptain) {
        response = await playerAPI.updateProfile({ phoneNumber: phoneNumber.trim() });
      } else {
        response = await api.put(`/teams/${teamId}/phone-number`, {
          phoneNumber: phoneNumber.trim(),
        });
      }

      if (response?.success !== false) {
        onSuccess({
          phoneNumber: phoneNumber.trim(),
          phone_number: phoneNumber.trim(),
        });
        onClose();
      } else {
        setError(response.message || t('teamDashboardPage.phoneSaveFail'));
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.message ||
        err.message ||
        t('teamDashboardPage.phoneSaveFail');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToProfile = () => {
    onClose();
    navigate('/player/settings/profile');
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('teamDashboardPage.phoneModalTitle')}</ModalTitle>
          <ModalSubtitle>{t('teamDashboardPage.phoneModalSubtitle')}</ModalSubtitle>
        </ModalHeader>

        <InfoBox>
          <InfoText>{t('teamDashboardPage.phoneModalPrivacy')}</InfoText>
        </InfoBox>

        {isPlayerCaptain ? (
          <>
            <InfoText style={{ marginBottom: '1rem', color: '#475569' }}>
              {t('teamDashboardPage.phoneModalCaptainProfileHint')}
            </InfoText>
            <ButtonContainer>
              <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
                {t('teamDashboardPage.phoneModalSkip')}
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleGoToProfile} disabled={isLoading}>
                {t('teamDashboardPage.phoneModalGoProfile')}
              </PrimaryButton>
            </ButtonContainer>
            <form onSubmit={handleSubmit} style={{ marginTop: '1.25rem' }}>
              <FormGroup>
                <Label htmlFor="phoneNumber">{t('teamDashboardPage.phoneModalLabel')}</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('teamDashboardPage.placeholderPhoneCh')}
                  disabled={isLoading}
                />
                {error && (
                  <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {error}
                  </div>
                )}
              </FormGroup>
              <PrimaryButton type="submit" disabled={isLoading} style={{ width: '100%' }}>
                {isLoading ? t('teamDashboardPage.saving') : t('teamDashboardPage.savePhoneNumber')}
              </PrimaryButton>
            </form>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="phoneNumber">{t('teamDashboardPage.phoneModalLabel')}</Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t('teamDashboardPage.placeholderPhoneCh')}
                disabled={isLoading}
              />
              {error && (
                <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {error}
                </div>
              )}
            </FormGroup>
            <ButtonContainer>
              <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
                {t('teamDashboardPage.phoneModalSkip')}
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isLoading}>
                {isLoading ? t('teamDashboardPage.saving') : t('teamDashboardPage.savePhoneNumber')}
              </PrimaryButton>
            </ButtonContainer>
          </form>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default PhoneNumberModal;
